// 메인 화면 배너 캐러셀.
// 작업계획서 12: 5초 자동 전환 + 수동 좌우 스와이프.
//
// 구현:
//  - 모든 배너를 가로 트랙(flex)에 깔고 transform: translateX 로 슬라이드 전환(애니메이션 포함).
//  - 자동 전환은 3초 간격. 사용자가 스와이프/도트 조작을 하면 타이머를 리셋해 충돌을 막는다.
//  - 터치 스와이프: 일정 거리 이상 끌면 이전/다음으로 이동. 짧게 탭하면 링크 이동.
//
// 접근성: prefers-reduced-motion 사용자에게는 슬라이드 트랜지션(애니메이션)만 끈다(멀미 회피).
//  - 과거에는 자동 전환 자체를 멈췄으나, 일부 모바일(절전/접근성 기본값으로 reduce-motion 이 켜진 기기)에서
//    배너가 전혀 넘어가지 않는 문제가 있어 자동 전환은 항상 유지하고 애니메이션만 생략하도록 바꿨다.
// 배너 클릭 시 link_kind 에 따라 분기 이동한다.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BannerDto } from "@oo/shared";
import { BANNER_LINK_KIND } from "@oo/shared";
import { imageUrl } from "./imageUrl.ts";

const AUTO_INTERVAL_MS = 3_000;
// 이 거리(px) 이상 끌면 스와이프로 간주하고 페이지를 넘긴다. 그 미만이면 탭(링크 이동)으로 처리.
const SWIPE_THRESHOLD_PX = 48;

interface Props {
  banners: BannerDto[];
}

// 메인 배너 캐러셀 컴포넌트.
export default function BannerCarousel({ banners }: Props) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  // 드래그 중 손가락을 따라 움직이는 보정값(px). 손을 떼면 0 으로 복귀.
  const [dragDx, setDragDx] = useState(0);
  const dragging = useRef(false);
  const startX = useRef(0);
  const movedRef = useRef(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  // 자동 전환 타이머. 수동 조작 시 리셋하기 위해 ref 로 들고 있는다.
  const timerRef = useRef<number | null>(null);

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // 자동 전환 타이머 (재시작 가능). 의존성/수동 조작이 바뀔 때마다 다시 건다.
  // tick 토큰을 증가시키면 effect 가 재실행되어 타이머가 리셋된다.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // 배너가 2장 이상이면 항상 자동 전환을 건다(reduce-motion 여부와 무관).
    // reduce-motion 은 아래 트랙 transition 에서만 반영해 애니메이션 없이 즉시 넘긴다.
    if (banners.length <= 1) {
      return;
    }
    timerRef.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % banners.length);
    }, AUTO_INTERVAL_MS);
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [banners.length, tick]);

  // banners 변경 시 인덱스 보정.
  useEffect(() => {
    if (index >= banners.length) {
      setIndex(0);
    }
  }, [banners.length, index]);

  if (banners.length === 0) {
    return null;
  }
  const current = banners[index];
  if (!current) {
    return null;
  }

  // 수동으로 특정 인덱스로 이동 + 자동 타이머 리셋.
  function goTo(next: number) {
    const len = banners.length;
    setIndex(((next % len) + len) % len);
    setTick((t) => t + 1);
  }

  function onClickBanner(banner: BannerDto) {
    // 스와이프 직후의 클릭은 무시한다(탭과 구분).
    if (movedRef.current) return;
    if (banner.linkKind === BANNER_LINK_KIND.PRODUCT && banner.linkTargetId) {
      navigate(`/product/${banner.linkTargetId}`);
    } else if (banner.linkKind === BANNER_LINK_KIND.CATEGORY && banner.linkTargetId) {
      navigate(`/category/${banner.linkTargetId}`);
    } else if (banner.linkKind === BANNER_LINK_KIND.EXTERNAL && banner.linkUrl) {
      window.open(banner.linkUrl, "_blank", "noopener,noreferrer");
    }
  }

  // 터치 스와이프 핸들러.
  function onTouchStart(e: React.TouchEvent) {
    if (banners.length <= 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    dragging.current = true;
    movedRef.current = false;
    startX.current = touch.clientX;
  }
  function onTouchMove(e: React.TouchEvent) {
    if (!dragging.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - startX.current;
    if (Math.abs(dx) > 8) {
      movedRef.current = true;
    }
    setDragDx(dx);
  }
  function onTouchEnd() {
    if (!dragging.current) return;
    dragging.current = false;
    const dx = dragDx;
    setDragDx(0);
    if (dx <= -SWIPE_THRESHOLD_PX) {
      goTo(index + 1);
    } else if (dx >= SWIPE_THRESHOLD_PX) {
      goTo(index - 1);
    }
  }
  // 스크롤 제스처로 전환되면 touchend 대신 touchcancel 이 온다.
  // 이때 드래그 상태를 정리하지 않으면 트랙 transition 이 계속 꺼진 채로 남으므로 반드시 리셋한다.
  function onTouchCancel() {
    dragging.current = false;
    setDragDx(0);
  }

  // 트랙 이동량: 기본은 index*100%, 드래그 중에는 손가락 이동(px)을 더한다.
  const viewportWidth = viewportRef.current?.clientWidth ?? 0;
  const dragPercent = viewportWidth > 0 ? (dragDx / viewportWidth) * 100 : 0;
  const translatePercent = -index * 100 + dragPercent;

  return (
    <section aria-label="이벤트 배너" className="flex flex-col gap-2">
      <div
        ref={viewportRef}
        className="relative aspect-[16/7] w-full overflow-hidden rounded-lg bg-ink-100"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        <div
          className="flex h-full w-full"
          style={{
            transform: `translateX(${translatePercent}%)`,
            // 드래그 중에는 손가락을 즉시 따라오도록 전환을 끈다. reduce-motion 도 전환 없음.
            transition: dragging.current || reduceMotion ? "none" : "transform 350ms ease",
          }}
        >
          {banners.map((b) => {
            const url = imageUrl(b.imageKey);
            const clickable = b.linkKind !== BANNER_LINK_KIND.NONE;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => clickable && onClickBanner(b)}
                disabled={!clickable}
                aria-hidden={b.id !== current.id}
                tabIndex={b.id === current.id ? 0 : -1}
                className={`relative h-full w-full shrink-0 ${clickable ? "cursor-pointer" : "cursor-default"}`}
              >
                {url ? (
                  // 메인 배너는 LCP 후보라 첫 장만 즉시 로드, 나머지는 지연 로드.
                  <img
                    src={url}
                    alt=""
                    draggable={false}
                    fetchPriority={b.id === current.id ? "high" : "auto"}
                    loading={b.id === current.id ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-ink-600">
                    배너 이미지 준비중
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {banners.length > 1 ? (
        <div className="flex justify-center gap-1.5" role="tablist" aria-label="배너 선택">
          {banners.map((b, i) => (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`배너 ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-6 bg-brand-600" : "w-2 bg-ink-300"
              }`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
